import { useMemo, useState } from 'react';
import styled from 'styled-components';

import DischargeConfirm from 'components/dischargeForm/DischargeConfirm';
import DischargeFileRequests from 'components/dischargeForm/DischargeFileRequests';
import DischargeInformation, {
  DischargeFormMeta,
  DischargeFormValues,
} from 'components/dischargeForm/DischargeInformation';
import DischargeQuestions, { DischargeQuestion } from 'components/dischargeForm/DischargeQuestions';
import { buildDischargeFormSteps, DischargeSteps } from 'components/dischargeForm/helpers';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { PatientName } from 'components/shared/StandardModal';
import { useForm } from 'context/form';
import { useProfile } from 'context/profile';
import { DocumentTypes } from 'models/Attachment';
import { ProviderGroupTypeConfig } from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind } from 'models/QuestionTemplate';

type Props = {
  patientName: string;
  locationEpisode: LocationEpisode;
  onCancel: () => void;
  dischargeQuestionsFlagEnabled: boolean;
  showActualDischargeDate?: boolean;
};

const REQUESTED_DOCS = [DocumentTypes.DischargeSummary];

export default function DischargeForm({
  locationEpisode,
  patientName,
  onCancel,
  dischargeQuestionsFlagEnabled,
  showActualDischargeDate = false,
}: Props) {
  const { profile } = useProfile();
  const userBelongsToPatientsOwningClient = useMemo(
    () => profile?.actingClientId === locationEpisode.owner.clientId,
    [profile?.actingClientId, locationEpisode.owner.clientId]
  );
  const isAffiliateUser = useMemo(() => profile?.isAffiliateUser, [profile]);
  const [step, setStep] = useState(DischargeSteps.Information);

  const groupTypeConfig = (locationEpisode.group.groupType.config ?? {}) as ProviderGroupTypeConfig;

  const { valid, dirty, values, submitForm, meta } = useForm<DischargeFormValues, DischargeFormMeta>();

  const questions = useMemo(
    () => (dischargeQuestionsFlagEnabled ? (locationEpisode.dischargeTemplate?.questions ?? []) : []),
    [dischargeQuestionsFlagEnabled, locationEpisode.dischargeTemplate?.questions]
  );

  const steps = useMemo(
    () =>
      buildDischargeFormSteps({
        hasDischargeQuestions: !!questions.length && !userBelongsToPatientsOwningClient && !isAffiliateUser,
        canContinueCare:
          !!values.dischargeReason?.dischargedToGroupType?.isProvider && values.dischargedGroup?.name !== 'Other',
        hasAllRequestedFiles: REQUESTED_DOCS.every((doc) =>
          (values.note?.attachments || []).some((a) => a.docType === doc)
        ),
        hideDischargeSummaryPrompt: groupTypeConfig.hideDischargeSummaryPrompt,
        currentStep: step,
      }),
    [
      step,
      questions.length,
      values.dischargedGroup,
      values.note?.attachments,
      values.dischargeReason,
      userBelongsToPatientsOwningClient,
      isAffiliateUser,
      groupTypeConfig.hideDischargeSummaryPrompt,
    ]
  );

  const hasMoreSteps = useMemo(() => steps.indexOf(step) < steps.length - 1, [step, steps]);

  const onPreviousStep = () => {
    if (step === DischargeSteps.Information) {
      onCancel();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex - 1]);
  };

  const onNextStep = () => {
    if (!hasMoreSteps) {
      submitForm();
      return;
    }

    const currentIndex = steps.indexOf(step);
    setStep(steps[currentIndex + 1]);
  };

  const dischargeQuestions: DischargeQuestion[] = useMemo(() => {
    const satisfactionScaleQuestions = questions.filter((q) => q.kind === QuestionKind.SATISFACTION_SCALE);
    const satisfactionScaleValues = satisfactionScaleQuestions.map((q) => values.questions?.[q.id]);

    return questions.map((q) => {
      if (
        q.config.optional ||
        // if satisfaction scale questions are present and none of their values match any of the note's
        // configured required ratings, make the note question optional on the form
        (satisfactionScaleQuestions.length &&
          q.kind === QuestionKind.NOTES &&
          q.config.requiredRatings?.length &&
          q.config.requiredRatings.every((rating) =>
            satisfactionScaleValues.every((value) => value !== `${rating} out of 5`)
          ))
      ) {
        return { ...q, optional: true };
      }

      return q;
    });
  }, [questions, values.questions]);

  const allRequiredQuestionsAnswered = useMemo(() => {
    return dischargeQuestions.every((q) => q.optional || values.questions?.[q.id]);
  }, [dischargeQuestions, values.questions]);

  return (
    <>
      <PatientName>Patient: {patientName}</PatientName>

      {step === DischargeSteps.Information && (
        <DischargeInformation
          locationEpisode={locationEpisode}
          showActualDischargeDate={showActualDischargeDate}
          hideDischargedAma={groupTypeConfig.hideDischargedAma}
        />
      )}
      {step === DischargeSteps.FileRequests && <DischargeFileRequests />}
      {step === DischargeSteps.Questions && <DischargeQuestions questions={dischargeQuestions} />}
      {step === DischargeSteps.Confirm && <DischargeConfirm />}

      <Actions>
        <Button variant='ghost' type='button' onClick={onPreviousStep}>
          {step === DischargeSteps.Information ? 'Cancel' : 'Back'}
        </Button>
        <Button
          type='button'
          disabled={
            !valid ||
            !dirty ||
            meta.isUploadingAttachments ||
            (step === DischargeSteps.Questions && !allRequiredQuestionsAnswered)
          }
          loading={meta.isSubmitting}
          onClick={onNextStep}>
          {hasMoreSteps ? 'Next' : 'Confirm'}
        </Button>
      </Actions>
    </>
  );
}

const Actions = styled(ButtonGroup)`
  margin-top: 24px;
  justify-content: flex-end;
`;
